import { useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import SingleBusinessPage from "../SingleBusinessPage";
import ConnectSinglePage from "./ConnectSinglePage";
import WebStoryDetailPage from "./WebStoryDetailPage";
import { saffronRout, saffronURL } from "../../../Url";

/** ✅ Section types from homepage vs actual single endpoints */
const TYPE_SINGLE_MAP = {
  trend_news: "news",
  latest_news: "news",
  featured_news: "news",
  trend_video: "videos",
  latest_video: "videos",
  featured_video: "videos",
  audio: "audios",
  blog: "blogs",
  ebook: "ebooks",
  feature: "features",

  popularProfiles: "people",
  listing: "listing",
  quotes: "quotes",
};

/** ✅ single API endpoints */
const getSingleApi = (type, slugOrId) => {
  const id = slugOrId;

  switch (type) {
    case "people":
      return `/api/v1/public/people-single/${id}`;
    case "listing":
      return `/api/v1/public/listing-single/${id}`;
    case "blogs":
      return `/api/v1/public/blog-single/${id}`;
    case "news":
      return `/api/v1/public/news-single/${id}`;
    case "videos":
      return `/api/v1/public/video-single/${id}`;
    case "audios":
      return `/api/v1/public/audio-single/${id}`;
    case "ebooks":
      return `/api/v1/public/ebook-single/${id}`;
    case "quotes":
      return `/api/v1/public/quote-single/${id}`;
    case "poll":
      return `/api/v1/public/poll-single/${id}`;
    case "webstories":
      return `/api/v1/public/web-story-single/${id}`;
    case "features":
      return `/api/v1/public/story-single/${id}`;
    case "webstory":
      return `/api/v1/public/web_stories-single/${id}`;
    default:
      return null;
  }
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) =>
  safeArray(arr).map((item, i) => ({ ...item, position: i }));

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;

  const d = payload?.data;
  if (Array.isArray(d)) return d;

  // common list keys
  if (Array.isArray(d?.people)) return d.people;
  if (Array.isArray(d?.listings)) return d.listings;
  if (Array.isArray(d?.blogs)) return d.blogs;
  if (Array.isArray(d?.news)) return d.news;
  if (Array.isArray(d?.videos)) return d.videos;
  if (Array.isArray(d?.audios)) return d.audios;
  if (Array.isArray(d?.ebooks)) return d.ebooks;
  if (Array.isArray(d?.quotes)) return d.quotes;
  if (Array.isArray(d?.polls)) return d.polls;
  if (Array.isArray(d?.webstories)) return d.webstories;
  if (Array.isArray(d?.stories)) return d.stories;
  if (Array.isArray(d?.web_stories)) return d.web_stories;

  return [];
};

export default function ConnectDetailsPage({
  api = saffronURL,
}) {
  const { type, slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const qs = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const sectionKey = location.state?.sectionKey || qs.get("section");

  const positionFromState = Number(location.state?.position ?? 0);

  /** ✅ normalize route type to single api type */
  const apiType = TYPE_SINGLE_MAP[type] || type;


  /** ✅ read activity context if coming from people activities */
  const activityType =
    location.state?.activityType || qs.get("activity") || null;
  const peopleId = location.state?.peopleId || qs.get("people_id") || null;

  /** ✅ SINGLE DATA */
  const { data: singleData, isLoading: singleLoading } = useQuery({
    queryKey: ["singleData", apiType, slug],
    queryFn: async () => {
      const endpoint = getSingleApi(apiType, slug);
      if (!endpoint) throw new Error("Invalid single endpoint");
      const res = await axios.get(`${api}${endpoint}`);
      return res.data;
    },
    enabled: !!apiType && !!slug,
    staleTime: 1000 * 60 * 60,
    retry: false,
  });



  /** ✅ SECTION LIST (for prev/next) */

  const { data: sectionListData = [] } = useQuery({
    queryKey: [
      "sectionListData",
      sectionKey,
      activityType,
      peopleId,
      location.search,
    ],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);

      // these are not API filters (internal navigation helpers)
      params.delete("section");
      params.delete("activity");

      // ✅ SECTION URLS
      // ✅ SECTION URLS
      const map = {
        // existing
        popularProfiles: `${api}/api/v1/public/get-feature-people?${params.toString()}&is_popular=1`,
        featuredProfiles: `${api}/api/v1/public/get-feature-people?${params.toString()}&is_featured=1`,
        listings: `${api}/api/v1/public/get-feature-listing?${params.toString()}&is_featured=1`,
        blogs: `${api}/api/v1/public/get-feature-blogs?${params.toString()}&is_featured=1`,
        trend_video: `${api}/api/v1/public/get-feature-videos?${params.toString()}&is_trending=1`,
        latest_video: `${api}/api/v1/public/get-feature-videos?${params.toString()}&is_latest=1`,
        featured_video: `${api}/api/v1/public/get-feature-videos?${params.toString()}&is_featured=1`,
        trend_news: `${api}/api/v1/public/get-feature-news?${params.toString()}&is_trending=1`,
        latest_news: `${api}/api/v1/public/get-feature-news?${params.toString()}&is_latest=1`,
        featured_news: `${api}/api/v1/public/get-feature-news?${params.toString()}&is_featured=1`,
        trend_audio: `${api}/api/v1/public/get-feature-audios?${params.toString()}&is_featured=1`,
        home_quotes: `${api}/api/v1/public/quotes?${params.toString()}`,
        home_features: `${api}/api/v1/public/features?${params.toString()}`,
        Home_listingsBusiness: `${api}/api/v1/public/listing?${params.toString()}`,

        // ✅ ADD THIS: NEWS LIST (use same list endpoint that list page uses)
        //   blogs: `${api}/api/v1/public/blogs?${params.toString()}`,

        // ✅ ADD THIS: VIDEOS/AUDIOS etc if you open details from list pages
        news: `${api}/api/v1/public/news?${params.toString()}`,
        videos: `${api}/api/v1/public/videos?${params.toString()}`,
        blogsList: `${api}/api/v1/public/blogs?${params.toString()}`,
        people: `${api}/api/v1/public/people?${params.toString()}`,
        web: `${api}/api/v1/public/web_stories?${params.toString()}`,
        audios: `${api}/api/v1/public/audios?${params.toString()}`,
        ebooks: `${api}/api/v1/public/ebooks?${params.toString()}`,
        quotes: `${api}/api/v1/public/quotes?${params.toString()}`,
        listingsList: `${api}/api/v1/public/listing?${params.toString()}`,
        polls: `${api}/api/v1/public/polls?${params.toString()}`,
        webstories: `${api}/api/v1/public/webstories?${params.toString()}`,
        features: `${api}/api/v1/public/features?${params.toString()}`,
      };

      // ✅ activities special: depends on activityType + peopleId
      if (sectionKey === "activities") {
        if (!activityType || !peopleId) return [];
        const p = new URLSearchParams(params);
        p.set("people_id", String(peopleId));
        const url = `${api}/api/v1/public/${activityType}?${p.toString()}`;
        const res = await axios.get(url);
        return normalizeArray(res.data);
      }

      const url = map[sectionKey];
      if (!url) return [];

      const res = await axios.get(url);
      return normalizeArray(res.data);
    },
    enabled: !!sectionKey,
    staleTime: 1000 * 60 * 60,
    retry: false,
  });

  const sectionItems = useMemo(
    () => addPosition(sectionListData),
    [sectionListData],
  );

  /** ✅ find current index */
  const getItemKey = (x) => String(x?.slug || x?.post_slug || x?.id || "");
  const currentKey = String(slug);
  const currentIndex = useMemo(() => {
    const idx = sectionItems.findIndex((x) => getItemKey(x) === currentKey);
    return idx >= 0 ? idx : positionFromState;
  }, [sectionItems, currentKey, positionFromState]);

  const gotoIndex = (idx) => {
    const item = sectionItems[idx];
    const nextSlug = item?.slug || item?.post_slug || item?.id;
    if (!nextSlug) return;

    navigate(
      {
        pathname: `${saffronRout}/details/${type}/${nextSlug}`,
        search: location.search,
      },
      {
        replace: true,
        state: { ...(location.state || {}), sectionKey, position: idx },
      },
    );

    window.scrollTo(0, 0);
  };

  const onPrev = () => {
    if (!sectionItems.length) return;
    const prev = currentIndex > 0 ? currentIndex - 1 : sectionItems.length - 1;
    gotoIndex(prev);
  };

  const onNext = () => {
    if (!sectionItems.length) return;
    const next = currentIndex < sectionItems.length - 1 ? currentIndex + 1 : 0;
    gotoIndex(next);
  };


  const onBack = () => {
    navigate(-1);
  };

  if (singleLoading)
    return <div className="py-10 text-center">Loading details....</div>;

  const canNavigate = !!sectionKey && sectionItems.length > 1;

  const raw = singleData?.data;

  // ✅ People response usually: { people: {...}, activities: {...} }
  const normalizedArticle = apiType === "people" ? raw?.people : raw;
  const normalizedActivities = raw?.activities;

  return apiType === "listing" ? (
    <SingleBusinessPage
      article={raw}
      onBack={onBack}
      onPrev={canNavigate ? onPrev : undefined}
      onNext={canNavigate ? onNext : undefined}
    />
  ) : apiType === "webstory" ? (
    <>
      <WebStoryDetailPage
        article={raw}
        onBack={onBack}
        onPrev={canNavigate ? onPrev : undefined}
        onNext={canNavigate ? onNext : undefined}
      />
    </>
  ) : (
    <>
      <ConnectSinglePage
        article={normalizedArticle}
        activities={normalizedActivities}
        onBack={onBack}
        onPrev={canNavigate ? onPrev : undefined}
        onNext={canNavigate ? onNext : undefined}
      />
    </>
  );
}
